/**
 * DebtReconcileSummary — Phase 85.1
 *
 * Post-import reconciliation card: "Debt payments found — 1 linked
 * automatically, 1 needs confirmation". Rendered after a statement import and
 * anywhere pending suggestions need attention.
 *
 * Honest by design: nothing here implies a lender-reported balance changed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/format';
import {
  confirmPaymentEvent, fetchCanonicalBalances, fetchReconciliationSummary,
  linkTransactionToLiability, rejectPaymentEvent,
} from '@/lib/liabilityReconcileApi';
import ConfirmLiabilityMatchDialog from './ConfirmLiabilityMatchDialog';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

export function DebtReconcileSummary({ importId = null, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [liabilities, setLiabilities] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, c] = await Promise.all([fetchReconciliationSummary(importId), fetchCanonicalBalances()]);
    setSummary(s);
    setLiabilities(Object.entries(c?.balances || {}).map(([id, v]) => ({ id, name: v.name || 'Loan' })));
    setLoading(false);
  }, [importId]);

  useEffect(() => { load(); }, [load]);

  const pending = (summary?.events || []).filter((e) => e.match_status === 'suggested');
  const linked = (summary?.events || []).filter((e) => e.match_status === 'auto_linked');
  const unmatched = summary?.unmatched_transactions || [];
  const total = pending.length + linked.length + unmatched.length;

  const handleConfirm = async (item, chosenLiabilityId) => {
    setBusy(true);
    try {
      if (item.event_id || item.id) {
        await confirmPaymentEvent(item.event_id || item.id, chosenLiabilityId || undefined);
      } else {
        await linkTransactionToLiability(item.transaction_id, chosenLiabilityId);
      }
      toast.success('Payment linked to your loan.');
      setActive(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Could not link that payment.');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (item) => {
    setBusy(true);
    try {
      if (item.event_id || item.id) await rejectPaymentEvent(item.event_id || item.id);
      setActive(null);
      await load();
      onChanged?.();
    } catch {
      toast.error('Could not update that payment.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2xl" data-testid="debt-reconcile-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking for loan payments…
        </CardContent>
      </Card>
    );
  }
  if (!summary?.available || total === 0) return null;

  const rows = [
    ...pending.map((e) => ({ ...e, _kind: 'pending' })),
    ...unmatched.map((u) => ({ ...u, _kind: 'unmatched' })),
  ];

  return (
    <>
      <Card className="rounded-2xl border-border/70" data-testid="debt-reconcile-summary">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <Landmark className="h-5 w-5 mt-0.5 text-primary" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-semibold" data-testid="debt-reconcile-title">Debt payments found</p>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="debt-reconcile-counts">
                {linked.length > 0 && `${linked.length} linked automatically`}
                {linked.length > 0 && pending.length > 0 && ' · '}
                {pending.length > 0 && `${pending.length} need${pending.length === 1 ? 's' : ''} confirmation`}
                {unmatched.length > 0 && `${(linked.length || pending.length) ? ' · ' : ''}${unmatched.length} unmatched`}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.event_id || r.id || r.transaction_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2.5"
                data-testid={`debt-reconcile-row-${r.transaction_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {formatMoney(r.total_payment ?? r.amount ?? 0, r.currency || 'INR')} ·{' '}
                    {r.description || 'Payment'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmtDate(r.payment_date || r.txn_date)}
                    {r._kind === 'pending' && r.liability_name ? ` · suggested: ${r.liability_name}` : ''}
                    {r._kind === 'unmatched' ? ' · no match found' : ''}
                  </p>
                </div>
                <Button
                  size="sm" variant={r._kind === 'pending' ? 'default' : 'secondary'}
                  onClick={() => setActive(r)}
                  data-testid={`debt-reconcile-review-${r.transaction_id}`}
                >
                  {r._kind === 'pending' ? 'Review' : 'Link to a loan'}
                </Button>
              </div>
            ))}
            {linked.map((e) => (
              <div
                key={e.event_id || e.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/50 px-3 py-2.5"
                data-testid={`debt-reconcile-linked-${e.transaction_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {formatMoney(e.total_payment || 0, e.currency || 'INR')} · {e.liability_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{fmtDate(e.payment_date)}</p>
                </div>
                <Badge variant="secondary" className="text-[10px]">Linked</Badge>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            Linking payments keeps loan activity, payoff estimates and linked goals in
            step with your statements. Balances your lender reported stay unchanged
            until you update them.
          </p>
        </CardContent>
      </Card>

      <ConfirmLiabilityMatchDialog
        open={!!active}
        onOpenChange={(v) => !v && setActive(null)}
        item={active}
        liabilities={liabilities}
        onConfirm={handleConfirm}
        onReject={handleReject}
        busy={busy}
      />
    </>
  );
}

export default DebtReconcileSummary;
